import { useCallback, useRef } from 'react';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { useCollectMapDiagnosticContext } from '../helpers/collect-map-diagnostic-context';
import type { MapHealthInputs } from '../helpers/map-health-monitor';

/**
 * The app state map health monitoring needs, read through a ref.
 *
 * Returns a stable function, so `useOwnedMapView` can take the latest values when a view
 * becomes ready without listing them as dependencies. Listing them would recreate a
 * working MapView — and its WebGL context — whenever a flag resolved or the API client
 * changed identity.
 */
export function useMapHealthInputs(): () => MapHealthInputs {
    const enabledFeatures = useEnabledFeatures();
    const collectDiagnosticContext = useCollectMapDiagnosticContext();

    const latestRef = useRef({ enabledFeatures, collectDiagnosticContext });
    latestRef.current = { enabledFeatures, collectDiagnosticContext };

    return useCallback(function getMapHealthInputs(): MapHealthInputs {
        return {
            enabledFeatures: latestRef.current.enabledFeatures,
            isDiagnosticContextEnabled: () =>
                latestRef.current.enabledFeatures.diagnosticContextEnabled === true,
            collectDiagnosticContext: (view, mapRef, isEmbeddedMap) =>
                latestRef.current.collectDiagnosticContext(view, mapRef, isEmbeddedMap),
        };
    }, []);
}
